import React, { useCallback, useState } from "react";
import { css } from "@linaria/core";
import cn from "classnames";
import { Markdown } from "@ui/Markdown";
import { MarkdownToolbarTable, ToolbarSeparator } from "@ui/Markdown/Toolbar/MarkdownToolbarTable";
import { ToolbarButton } from "@ui/Markdown/Toolbar/ToolbarButton";
import { Attachment2 } from "@ui/IconV2/react";
import Button from "@ui/Button";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import MaxCharacterPlugin from "@ui/Markdown/plugins/common/MaxCharacterPlugin";
import { DEFAULT_CONFIG_TABLE } from "@ui/Markdown/plugins/Table/utils/config";
import { DEFAULT_CLASSNAME_THEME_TABLE } from "@ui/Markdown/plugins/Table/utils/style";
import { spacings } from "@ui/Theme";
import uiT from "@ui/i18n";
import t from "../../../i18n";
import SetupCommentToggle from "./SetupCommentToggle";
import { setupCommentEditor } from "../styles/messageEditorStyles";
import type { CommentType } from "../../../api/useComments";

export default function EditComment({
  content: initialContent,
  type: initialType,
  canChangeType,
  onTypeChange,
  onSave,
  onCancel,
  maxLength,
}: {
  content: string;
  type: CommentType;
  canChangeType: boolean;
  onTypeChange: (type: CommentType) => void;
  onSave: (content: string, type?: CommentType) => Promise<void>;
  onCancel: () => void;
  maxLength?: number;
}) {
  const [content, setContent] = useState(initialContent);
  const [type, setType] = useState(initialType);
  const [saving, setSaving] = useState(false);
  const [tooLong, setTooLong] = useState(false);
  const dirty = content !== initialContent || (canChangeType && type !== initialType);
  const canSave = !saving && dirty && content.trim().length > 0 && !tooLong;
  const toggleType = () => {
    if (!canChangeType || saving) return;
    const nextType = type === "SETUP" ? "RUN" : "SETUP";
    setType(nextType);
    onTypeChange(nextType);
  };
  const handleCharacterCount = useCallback(
    (count: number) => {
      setTooLong(maxLength !== undefined && count >= maxLength);
    },
    [maxLength],
  );
  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await onSave(content, canChangeType ? type : undefined);
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className={cn({ [setupCommentEditor]: type === "SETUP" })}>
      <Markdown
        editable
        defaultValue={initialContent}
        onConversion={setContent}
        error={tooLong}
        disabled={saving}
        config={{ nodes: [...DEFAULT_CONFIG_TABLE.nodes], theme: DEFAULT_CONFIG_TABLE.theme }}
        theme={DEFAULT_CLASSNAME_THEME_TABLE}
      >
        {richText => (
          <>
            <MarkdownToolbarTable>
              <ToolbarSeparator />
              <ToolbarButton description={t("comments.tooltip.feature_under_development")} disabled>
                <Attachment2 size="s" />
              </ToolbarButton>
            </MarkdownToolbarTable>
            {richText}
            {maxLength !== undefined && (
              <MaxCharacterPlugin
                maxCharacters={maxLength}
                onCharacterCountChange={handleCharacterCount}
              />
            )}
          </>
        )}
      </Markdown>
      {canChangeType && (
        <SetupCommentToggle checked={type === "SETUP"} onChange={toggleType} disabled={saving} />
      )}
      <footer className={actions}>
        {tooLong && (
          <AdditionnalMessage
            status="ERROR"
            text={uiT("project.dashboard.block.comment_rich_text.error.text_too_long")}
          />
        )}
        <Button variant="ghost" onClick={onCancel} disabled={saving} slim>
          {uiT("message_editor.cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave} slim>
          {uiT("message_editor.publish")}
        </Button>
      </footer>
    </div>
  );
}
const actions = css`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${spacings.xs};
  margin-top: ${spacings.xs};
`;
